import { useEffect, useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Search } from "lucide-react";
import type { Jednotka, PantryItem, Ulozisko } from "../types";
import { toDateInputValue } from "../utils";

interface ItemDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  klientId: number;
  uloziska: Ulozisko[];
  editItem?: PantryItem | null;
  onSaved: () => void;
}

export function ItemDialog({
  open,
  onOpenChange,
  klientId,
  uloziska,
  editItem,
  onSaved,
}: ItemDialogProps) {
  const { t } = useTranslation();
  const { data: potraviny = [] } = trpc.potravina.listAll.useQuery();
  const [foodSearch, setFoodSearch] = useState("");
  const [foodOpen, setFoodOpen] = useState(false);
  const [selectedFood, setSelectedFood] = useState<{
    id: number;
    nazov: string;
    balenieG: string | null;
  } | null>(
    editItem
      ? {
          id: editItem.potravinaId,
          nazov: editItem.potravina.nazov,
          balenieG: editItem.potravina.balenieG,
        }
      : null
  );
  const [mnozstvo, setMnozstvo] = useState(editItem?.mnozstvo ?? "1");
  const [jednotka, setJednotka] = useState<Jednotka>(
    (editItem?.jednotka as Jednotka) ?? "ks"
  );
  const [balenieG, setBalenieG] = useState(
    editItem?.balenieG ?? editItem?.potravina.balenieG ?? ""
  );
  const [datumSpotreby, setDatumSpotreby] = useState(
    toDateInputValue(editItem?.datumSpotreby)
  );
  const [uloziskoId, setUloziskoId] = useState<number | null>(
    editItem?.uloziskoId ?? uloziska[0]?.id ?? null
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editItem) {
      setSelectedFood({
        id: editItem.potravinaId,
        nazov: editItem.potravina.nazov,
        balenieG: editItem.potravina.balenieG,
      });
      setMnozstvo(editItem.mnozstvo ?? "1");
      setJednotka((editItem.jednotka as Jednotka) ?? "ks");
      setBalenieG(editItem.balenieG ?? editItem.potravina.balenieG ?? "");
      setDatumSpotreby(toDateInputValue(editItem.datumSpotreby));
      setUloziskoId(editItem.uloziskoId ?? uloziska[0]?.id ?? null);
    } else {
      setSelectedFood(null);
      setFoodSearch("");
      setMnozstvo("1");
      setJednotka("ks");
      setBalenieG("");
      setDatumSpotreby("");
      setUloziskoId(uloziska[0]?.id ?? null);
    }
    setSaving(false);
  }, [open, editItem]); // eslint-disable-line react-hooks/exhaustive-deps

  const addMutation = trpc.spajza.addPolozka.useMutation();
  const updateMutation = trpc.spajza.updatePolozka.useMutation();
  const updateBalenieG = trpc.potravina.updateBalenieG.useMutation();

  const filteredFoods = useMemo(() => {
    const q = foodSearch.toLowerCase();
    return potraviny
      .filter(p => p.nazov.toLowerCase().includes(q))
      .slice(0, 50);
  }, [potraviny, foodSearch]);

  const needsBalenieG =
    (jednotka === "ks" || jednotka === "balenie") &&
    !selectedFood?.balenieG &&
    !balenieG;

  const handleSave = async () => {
    if (!editItem && !selectedFood) {
      toast.error(t("mealLibrary:spajza.selectFoodRequired"));
      return;
    }
    if (!mnozstvo || parseFloat(mnozstvo) <= 0) {
      toast.error(t("mealLibrary:spajza.validAmountRequired"));
      return;
    }
    if (
      (jednotka === "ks" || jednotka === "balenie") &&
      !balenieG &&
      !selectedFood?.balenieG
    ) {
      toast.error(t("mealLibrary:spajza.packageSizeRequired"));
      return;
    }
    setSaving(true);
    try {
      const balenieGVal = balenieG || selectedFood?.balenieG || null;

      if (editItem) {
        await updateMutation.mutateAsync({
          id: editItem.id,
          klientId,
          mnozstvo,
          jednotka,
          balenieG: balenieGVal,
          datumSpotreby: datumSpotreby || null,
          uloziskoId,
        });
        toast.success(t("mealLibrary:spajza.itemUpdated"));
      } else {
        await addMutation.mutateAsync({
          klientId,
          potravinaId: selectedFood!.id,
          uloziskoId,
          mnozstvo,
          jednotka,
          balenieG: balenieGVal,
          datumSpotreby: datumSpotreby || null,
        });
        if (balenieG && !selectedFood?.balenieG) {
          await updateBalenieG
            .mutateAsync({ id: selectedFood!.id, balenieG })
            .catch(() => {});
        }
        toast.success(t("mealLibrary:spajza.itemAdded"));
      }
      onSaved();
      onOpenChange(false);
    } catch {
      toast.error(t("mealLibrary:spajza.itemSaveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {editItem
              ? t("mealLibrary:spajza.editItemWithName", {
                  name:
                    editItem.potravina?.nazov ??
                    t("mealLibrary:spajza.itemFallback"),
                })
              : t("mealLibrary:spajza.addItem")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 overflow-y-auto flex-1 pr-1">
          {!editItem && (
            <div className="space-y-1">
              <Label>{t("mealLibrary:spajza.foodRequired")}</Label>
              <Popover open={foodOpen} onOpenChange={setFoodOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start font-normal"
                  >
                    {selectedFood ? (
                      selectedFood.nazov
                    ) : (
                      <span className="text-muted-foreground flex items-center gap-2">
                        <Search className="h-4 w-4" />
                        {t("mealLibrary:spajza.search")}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[360px] p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder={t("mealLibrary:spajza.search")}
                      value={foodSearch}
                      onValueChange={setFoodSearch}
                    />
                    <CommandList className="max-h-56 overflow-y-auto">
                      <CommandEmpty>
                        {t("mealLibrary:spajza.noFood")}
                      </CommandEmpty>
                      <CommandGroup>
                        {filteredFoods.map(p => (
                          <CommandItem
                            key={p.id}
                            value={p.nazov}
                            onSelect={() => {
                              setSelectedFood({
                                id: p.id!,
                                nazov: p.nazov,
                                balenieG: p.balenieG ?? null,
                              });
                              setBalenieG(p.balenieG ?? "");
                              setFoodOpen(false);
                            }}
                          >
                            {p.nazov}
                            {p.balenieG && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                {p.balenieG}{" "}
                                {t("mealLibrary:spajza.gramsPerPackage")}
                              </span>
                            )}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>{t("mealLibrary:spajza.amountRequired")}</Label>
              <Input
                type="number"
                value={mnozstvo}
                onChange={e => setMnozstvo(e.target.value)}
                min="0.01"
                step="0.01"
                placeholder="1"
              />
            </div>
            <div className="space-y-1">
              <Label>{t("mealLibrary:spajza.unitRequired")}</Label>
              <Select
                value={jednotka}
                onValueChange={v => setJednotka(v as Jednotka)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ks">
                    {t("mealLibrary:spajza.unitKsFull")}
                  </SelectItem>
                  <SelectItem value="balenie">
                    {t("mealLibrary:spajza.unitBalenie")}
                  </SelectItem>
                  <SelectItem value="g">
                    {t("mealLibrary:spajza.unitGFull")}
                  </SelectItem>
                  <SelectItem value="kg">
                    {t("mealLibrary:spajza.unitKgFull")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {(jednotka === "ks" || jednotka === "balenie") && (
            <div className="space-y-1">
              <Label>
                {t("mealLibrary:spajza.packageSize")}{" "}
                {needsBalenieG && <span className="text-destructive">*</span>}
              </Label>
              <Input
                type="number"
                value={balenieG}
                onChange={e => setBalenieG(e.target.value)}
                placeholder={
                  selectedFood?.balenieG ??
                  t("mealLibrary:spajza.packageSizePlaceholder")
                }
                min="0.1"
                step="0.1"
              />
              <p className="text-xs text-muted-foreground">
                {t("mealLibrary:spajza.packageSizeHint")}
              </p>
            </div>
          )}
          <div className="space-y-1">
            <Label>{t("mealLibrary:spajza.expiryDateOptional")}</Label>
            <Input
              type="date"
              value={datumSpotreby}
              onChange={e => setDatumSpotreby(e.target.value)}
            />
          </div>
          {uloziska.length > 0 && (
            <div className="space-y-1">
              <Label>{t("mealLibrary:spajza.storage")}</Label>
              <Select
                value={uloziskoId ? String(uloziskoId) : "none"}
                onValueChange={v =>
                  setUloziskoId(v === "none" ? null : Number(v))
                }
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t("mealLibrary:spajza.noStorage")}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    {t("mealLibrary:spajza.noStorage")}
                  </SelectItem>
                  {uloziska.map(u => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {u.nazov}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter className="pt-4 border-t mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common:cancel")}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving
              ? t("mealLibrary:sablona.saving")
              : editItem
                ? t("mealLibrary:dialog.save")
                : t("mealLibrary:spajza.addItem")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
